/**
 * AERO - Contact page
 *
 * Two things live here: the pure view logic for the 01/02 chooser (exported so
 * it can be unit-tested under Node without a DOM, the same shape as
 * assets/help.js and assets/cart-utils.js) and the DOM layer that drives the
 * page — scroll reveals, the chooser, and client-side form validation.
 *
 * El envío es nativo a propósito. Antes este archivo hacía POST con fetch() y
 * llamaba a showSuccess() tanto desde .then() como desde .catch(), sin mirar
 * response.ok: un rechazo de Shopify (spam, reCAPTCHA, red caída) mostraba
 * igualmente "MESSAGE SENT!" y el mensaje se perdía sin que nadie se enterara.
 * Ahora el navegador envía el formulario, Shopify decide, y quien renderiza el
 * resultado es templates/page.contact.liquid con form.posted_successfully? y
 * form.errors. La validación de aquí solo evita viajes de ida y vuelta
 * inútiles; no decide nada sobre el resultado.
 */
(function () {
  'use strict';

  /* ===== Pure logic (no DOM access below this line) ===== */

  /*
   * Reduce a location.hash to a bare, comparable token: lowercased, without
   * the leading '#', trimmed. Anything that is not a string (null, undefined,
   * a number) becomes '', so callers never have to type-check first.
   */
  function normalizeHash(hash) {
    if (typeof hash !== 'string') return '';
    return hash.trim().replace(/^#+/, '').trim().toLowerCase();
  }

  /*
   * Decide which of the two regions is open.
   *
   * `serverState` is 'errors' when Shopify rejected the submission and the
   * template rendered the error block, 'idle' otherwise.
   *
   * The server wins over the URL on purpose: a rejected submission has to
   * reopen the panel with the user's text intact, whatever the hash says.
   * Bouncing them back to the cards would throw the message away.
   */
  /*
   * Los dos tokens que abren el formulario. 'help' es el canónico, el que
   * empuja pushState. 'aero-contact-form-wrap' es el href real de la tarjeta
   * 02, y un ctrl+clic la abre en una pestaña nueva sin pasar por el listener:
   * si el router no lo reconociera, esa pestaña mostraría el selector con el
   * ancla pedida en display:none.
   */
  var FORM_HASHES = ['help', 'aero-contact-form-wrap'];

  function resolveContactView(hash, serverState) {
    if (serverState === 'errors') return 'form';
    if (FORM_HASHES.indexOf(normalizeHash(hash)) !== -1) return 'form';
    return 'choice';
  }

  /* The hash that represents a view. 'choice' is the bare URL, not '#choice'. */
  function hashForView(view) {
    return view === 'form' ? '#help' : '';
  }

  var api = {
    normalizeHash: normalizeHash,
    resolveContactView: resolveContactView,
    hashForView: hashForView,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.AeroContactChooser = api;

  /* ===== DOM layer ===== */

  if (typeof document === 'undefined') return;

  function prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    var query = window.matchMedia('(prefers-reduced-motion: reduce)');
    return !!(query && query.matches);
  }

  /* A jump is the honest fallback: 'smooth' is only ever a request. */
  function scrollToEl(el, block, allowSmooth) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    var behavior = allowSmooth && !prefersReducedMotion() ? 'smooth' : 'auto';
    el.scrollIntoView({ behavior: behavior, block: block });
  }

  /* focus() scrolls by itself, which fights the smooth scroll that follows. */
  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  }

  function init() {
    /* ===== Scroll-reveal animation ===== */
    var revealEls = document.querySelectorAll('.aero-contact__reveal');
    if (revealEls.length && typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
      revealEls.forEach(function (el) {
        io.observe(el);
      });
    }

    /* ===== 01/02 chooser =====
       Todo puede faltar: el estado de éxito no renderiza selector ni panel. */
    var chooser = document.querySelector('[data-contact-choice]');
    var panel = document.querySelector('[data-contact-form-body]');
    var openCard = document.querySelector('[data-contact-open]');
    var backBtn = document.querySelector('[data-contact-back]');

    /* Llevar el foco al resultado que ha renderizado el servidor. */
    var posted = document.getElementById('contactSuccess');
    var serverErrors = document.getElementById('contactErrors');
    var landing = posted || serverErrors;

    /*
     * El estado del servidor se lee del propio markup. La ausencia de
     * data-collapsed en el panel no sirve como señal — el JS mismo la quita en
     * cuanto abre el formulario. El bloque de errores, en cambio, solo existe
     * si Shopify ha rechazado el envío.
     */
    var serverState = serverErrors ? 'errors' : 'idle';

    /*
     * Abrir/cerrar es solo JS: la regla que oculta [data-collapsed] vive en
     * @media (scripting: enabled). Sin JS el lector ve las tarjetas y el
     * formulario apilados, y la tarjeta 02 es un ancla normal al panel.
     */
    function applyView(view, options) {
      var opts = options || {};
      var target;

      if (view === 'form') {
        if (panel) {
          panel.removeAttribute('data-collapsed');
          /*
           * Obligatorio: .aero-contact__reveal arranca en opacity 0 y un
           * elemento en display:none nunca intersecta, así que el
           * IntersectionObserver de arriba jamás dispararía para el panel y se
           * quedaría invisible para siempre. Se reutiliza el par
           * .aero-contact__reveal / .is-visible, sin keyframes nuevos.
           */
          panel.classList.add('is-visible');
        }
        if (chooser) chooser.setAttribute('data-collapsed', '');
        target = panel;
      } else {
        if (chooser) {
          chooser.removeAttribute('data-collapsed');
          /* Mismo motivo: con errores del servidor el selector nace plegado. */
          chooser.classList.add('is-visible');
        }
        if (panel) panel.setAttribute('data-collapsed', '');
        target = chooser;
      }

      if (opts.focus) {
        /* Al volver, el foco aterriza en la tarjeta que abrió el panel. */
        focusEl(view === 'form' ? panel : openCard || chooser);
      }
      if (opts.scroll) scrollToEl(target, 'start', opts.smooth !== false);
    }

    function urlForView(view) {
      return window.location.pathname + window.location.search + api.hashForView(view);
    }

    function pushView(view) {
      if (!window.history || typeof window.history.pushState !== 'function') return;
      window.history.pushState({ aeroContactView: view }, '', urlForView(view));
    }

    if (chooser || panel) {
      /* Estado inicial. Si el servidor ha renderizado un resultado, el foco y
         el scroll son suyos (justo debajo), no del selector. */
      var initialView = api.resolveContactView(window.location.hash, serverState);
      /*
       * En una carga normal no se toca ni el foco ni el scroll. Aterrizar el
       * foco en la tarjeta 02 y empujar el <h1> fuera de pantalla en cada
       * visita es un salto que nadie ha pedido. Solo se aterriza cuando el
       * formulario se ha pedido explícitamente por hash, y sin animarlo: un
       * scroll suave durante la carga se pelea con el del propio navegador.
       */
      var deepLinked = !landing && initialView === 'form';
      applyView(initialView, { focus: deepLinked, scroll: deepLinked, smooth: false });

      if (openCard) {
        openCard.addEventListener('click', function (event) {
          /* Un clic modificado abre pestaña nueva; ahí no mandamos nosotros. */
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          pushView('form');
          applyView('form', { focus: true, scroll: true, smooth: true });
        });
      }

      if (backBtn) {
        backBtn.addEventListener('click', function () {
          pushView('choice');
          applyView('choice', { focus: true, scroll: true, smooth: true });
        });
      }

      /* Atrás/adelante del navegador: se reaplica sin empujar más historial y
         sin animar el scroll, que en una navegación de historial molesta. */
      window.addEventListener('popstate', function () {
        applyView(api.resolveContactView(window.location.hash, serverState), {
          focus: true,
          scroll: true,
          smooth: false,
        });
      });
    }

    if (landing) {
      focusEl(landing);
      scrollToEl(landing, 'center', true);
    }

    /* ===== Form logic ===== */
    var form = document.getElementById('aero-contact-form');
    if (!form) return;

    var submitBtn = form.querySelector('.aero-contact__submit');

    function inputOf(field) {
      return (
        field.querySelector('.aero-contact__input') ||
        field.querySelector('.aero-contact__select') ||
        field.querySelector('.aero-contact__textarea')
      );
    }

    /**
     * Validate a single field and toggle its error state.
     * @param {HTMLElement} field — the .aero-contact__field container
     * @returns {boolean}
     */
    function validateField(field) {
      var input = inputOf(field);
      if (!input) return true;

      var isValid = true;

      if (input.hasAttribute('required') && !input.value.trim()) {
        isValid = false;
      }

      if (input.type === 'email' && input.value.trim()) {
        var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(input.value.trim())) isValid = false;
      }

      field.classList.toggle('has-error', !isValid);
      input.setAttribute('aria-invalid', isValid ? 'false' : 'true');
      return isValid;
    }

    // Real-time validation on blur
    form.querySelectorAll('.aero-contact__field').forEach(function (field) {
      var input = inputOf(field);
      if (!input) return;

      input.addEventListener('blur', function () {
        validateField(field);
      });
      // Clear error on input
      input.addEventListener('input', function () {
        if (field.classList.contains('has-error')) validateField(field);
      });
    });

    /* ===== Submit gate =====
       Solo se cancela el envío si algo está mal. Si todo está bien el evento
       sigue su curso y el navegador hace el POST de verdad. */
    form.addEventListener('submit', function (e) {
      var fields = form.querySelectorAll('.aero-contact__field');
      var firstInvalid = null;

      fields.forEach(function (field) {
        if (!validateField(field) && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        e.preventDefault();
        /* Si el panel estuviera plegado no habría nada que enfocar. */
        applyView('form', { focus: false, scroll: false });
        var input = inputOf(firstInvalid);
        if (input) input.focus();
        return;
      }

      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
      }
    });

    /* Volver atrás restaura la página desde la bfcache con el botón todavía
       deshabilitado, así que se rearma al mostrarse. */
    window.addEventListener('pageshow', function () {
      if (!submitBtn) return;
      submitBtn.classList.remove('is-loading');
      submitBtn.disabled = false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
